import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { fetchAllPermissions } from "../../app/permissionSlice";
import { Spin } from "antd";
import { toast } from "react-toastify";
import { createNewRole, updateRoleById } from "../../app/roleSlice";
import { useLocation, useNavigate } from "react-router-dom";

type Permission = {
  id: number;
  permission_key: string;
  display_name: string;
};

const RoleManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const passedRole = location.state?.role;
  const {loading: permLoading,error: permError} = useSelector((state: RootState) => state.permission);
  const [permissions, setFilteredPermissions] = useState<Permission[]>([]);
  const [accessPermissions, setAccessPermissions] = useState<Permission[]>([]);
  const initialFormData = {
    name: "",
    display_name: "",
    description: "",
    permissions: [] as number[],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const response = await dispatch(fetchAllPermissions()).unwrap();
        const allPerms: Permission[] = Array.isArray(response)
          ? response
          : Array.isArray(response.data)
          ? response.data
          : [];

        // const localPermissionKeys = JSON.parse(
        //   localStorage.getItem("permissions") || "[]"
        // ) as string[];

        // const filtered = allPerms.filter((perm) =>
        //   localPermissionKeys.includes(perm.permission_key)
        // );

        const accessPerms = allPerms.filter((perm) =>
          ["web_access", "app_access"].includes(perm.permission_key)
        );

        const filteredWithoutAccess = allPerms.filter(
          (perm) => !["web_access", "app_access"].includes(perm.permission_key)
        );

        setFilteredPermissions(filteredWithoutAccess);
        setAccessPermissions(accessPerms);
      } catch (error) {
        toast.error("Failed to fetch permissions");
      }
    };

    loadPermissions();

    if (passedRole) {
      setFormData({
        name: passedRole.role_name || "",
        display_name: passedRole.display_name || "",
        description: passedRole.description || "",
        permissions: passedRole.permissions?.map((p: any) => p.id) || [],
      });
    } else {
      setFormData(initialFormData);
      setErrors({})
    }
  }, [dispatch, passedRole]);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value.trimStart() }));
  };

  const groupPermissionsByModule = () => {
    return permissions.reduce((acc, perm) => {
      const key = perm.permission_key.split("-")[0] || "Others";
      if (!acc[key]) acc[key] = [];
      acc[key].push(perm);
      return acc;
    }, {} as Record<string, Permission[]>);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors: { [key: string]: string } = {};
    if (!formData.display_name.trim())
      validationErrors.display_name = "Role Name is required";
    if (formData.permissions.length === 0)
      validationErrors.permissions = "Please select at least one permission";

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const payload = {
        role_name: formData.name,
        display_name: formData.display_name,
        permission_ids: formData.permissions,
      };

      let resultAction;

      if (passedRole) {
        const formDataToSend = new FormData();
        formDataToSend.append("role_name", payload.role_name);
        formDataToSend.append("display_name", payload.display_name);
        payload.permission_ids.forEach((id) =>
          formDataToSend.append("permission_ids[]", id.toString())
        );

        resultAction = await dispatch(
          updateRoleById({ id: passedRole.id, formData: formDataToSend })
        ).unwrap();
      } else {
        resultAction = await dispatch(createNewRole(payload)).unwrap();
      }

      toast.success(
        resultAction.message ||
          `Role ${passedRole ? "updated" : "created"} successfully`
      );
      navigate("/dashboard/rolemanagement");
    } catch (error: any) {
      toast.error(
        error || `Failed to ${passedRole ? "update" : "create"} role`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAll = (checked: boolean) => {
    const allIds = [...permissions, ...accessPermissions].map((p) => p.id);
    setFormData((prev) => ({
      ...prev,
      permissions: checked ? allIds : [],
    }));
  };
  const toTitleCase = (str: string) =>
    str
      .replace(/[-_]/g, " ")
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));

  return (
    <>
      <Breadcrumb pageName={passedRole ? "Update Role" : "Create Role"} />
      <div className="bg-white dark:bg-boxdark p-6 rounded-md shadow-md max-w-7xl mx-auto">
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="md:col-span-1">
            <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="display_name"
              maxLength={50}
              value={formData.display_name}
              onChange={handleChange}
              placeholder="Enter Role Name"
              className={`w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                errors.display_name ? "border-red-500" : "border-stroke"
              }`}
            />
            {errors.display_name && (
              <p className="text-red-500 text-sm mt-1">{errors.display_name}</p>
            )}
          </div>

          {/* Permissions */}
          <div className="md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-medium text-gray-700 dark:text-white">
                Assign Permissions <span className="text-red-500">*</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={
                    permissions.length + accessPermissions.length > 0 &&
                    [...permissions, ...accessPermissions].every((p) =>
                      formData.permissions.includes(p.id)
                    )
                  }
                  onChange={(e) => handleToggleAll(e.target.checked)}
                  className="rounded border-gray-300 text-primary focus:ring-0"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white">
                  Select All
                </span>
              </label>
            </div>

            {permLoading ? (
              <div className="flex justify-center items-center h-32">
                <Spin />
              </div>
            ) : permError ? (
              <p className="text-red-500">{permError}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white text-sm">
                      <tr>
                        <th className="py-4 px-4 text-left font-semibold">
                          Module
                        </th>
                      {["Create", "Update", "Delete", "View"].map((action) => {
                            const allActionPerms = permissions.filter((p) =>
                              p.display_name
                                .toLowerCase()
                                .includes(action.toLowerCase())
                            );
                            const isAllSelected =
                              allActionPerms.length > 0 &&
                              allActionPerms.every((p) =>
                                formData.permissions.includes(p.id)
                              );

                            return (
                              <th
                                key={action}
                                className="py-4 px-4 text-center font-semibold"
                              >
                                <label className="flex items-center justify-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    onChange={(e) => {
                                      const updated = [...formData.permissions];
                                  const ids = allActionPerms.map((p) => p.id);

                                      e.target.checked
                                        ? ids.forEach((id) => {
                                            if (!updated.includes(id))
                                              updated.push(id);
                                          })
                                        : ids.forEach((id) => {
                                            const i = updated.indexOf(id);
                                            if (i !== -1) updated.splice(i, 1);
                                          });

                                      setFormData((prev) => ({
                                        ...prev,
                                        permissions: updated,
                                      }));
                                    }}
                                    className="rounded border-gray-300 text-primary focus:ring-0"
                                  />
                                  <span>{action}</span>
                                </label>
                              </th>
                            );
                      })}
                      </tr>
                    </thead>

                    <tbody className="text-sm text-gray-700 dark:text-gray-200 divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(groupPermissionsByModule()).map(
                        ([moduleName, perms]) => (
                          <tr
                            key={moduleName}
                            className="bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                          >
                            <td className="py-3 px-4 font-medium">
                              {toTitleCase(moduleName)} Permission
                            </td>
                            {["Create", "Update", "Delete", "View"].map(
                              (action) => {
                                const perm = perms.find((p) =>
                                  p.display_name
                                    .toLowerCase()
                                    .includes(action.toLowerCase())
                                );
                                return (
                                  <td
                                    key={action}
                                    className="py-3 px-4 text-center"
                                  >
                                    {perm ? (
                                      <input
                                        type="checkbox"
                                        checked={formData.permissions.includes(
                                          perm.id
                                        )}
                                        onChange={(e) => {
                                          const updated = e.target.checked
                                            ? [...formData.permissions, perm.id]
                                            : formData.permissions.filter(
                                                (id) => id !== perm.id
                                              );
                                          setFormData((prev) => ({
                                            ...prev,
                                            permissions: updated,
                                          }));
                                        }}
                                        className="rounded border-gray-300 text-primary focus:ring-0"
                                      />
                                    ) : (
                                      <span className="italic text-gray-400">
                                        —
                                      </span>
                                    )}
                                  </td>
                                );
                              }
                            )}
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  {accessPermissions.length > 0 && (
                    <div className="mt-6 ">
                      <label className="text-sm font-medium text-gray-700 dark:text-white">
                      Access Permissions <span className="text-red-500">*</span>
                      </label>

                      <table className="w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mt-4">
                        <thead className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white text-sm">
                          <tr>
                            <th className="py-3 px-4 text-left font-semibold">
                              Permission Name
                            </th>
                            <th className="py-3 px-4 text-center font-semibold">
                              Web Access
                            </th>
                            <th className="py-3 px-4 text-center font-semibold">
                              App Access
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="bg-white dark:bg-slate-800">
                            <td className="py-3 px-4 font-medium">
                              Access Permission
                            </td>
                            <td className="py-3 px-4 text-center">
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(
                                  accessPermissions.find(
                                    (p) => p.permission_key === "web_access"
                                  )?.id || -1
                                )}
                                onChange={(e) => {
                                  const webPerm = accessPermissions.find(
                                    (p) => p.permission_key === "web_access"
                                  );
                                  if (!webPerm) return;
                                  const updated = e.target.checked
                                    ? [...formData.permissions, webPerm.id]
                                    : formData.permissions.filter(
                                        (id) => id !== webPerm.id
                                      );
                                  setFormData((prev) => ({
                                    ...prev,
                                    permissions: updated,
                                  }));
                                }}
                                className="rounded border-gray-300 text-primary focus:ring-0"
                              />
                            </td>
                            <td className="py-3 px-4 text-center">
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(
                                  accessPermissions.find(
                                    (p) => p.permission_key === "app_access"
                                  )?.id || -1
                                )}
                                onChange={(e) => {
                                  const appPerm = accessPermissions.find(
                                    (p) => p.permission_key === "app_access"
                                  );
                                  if (!appPerm) return;
                                  const updated = e.target.checked
                                    ? [...formData.permissions, appPerm.id]
                                    : formData.permissions.filter(
                                        (id) => id !== appPerm.id
                                      );
                                  setFormData((prev) => ({
                                    ...prev,
                                    permissions: updated,
                                  }));
                                }}
                                className="rounded border-gray-300 text-primary focus:ring-0"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {errors.permissions && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.permissions}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-black text-white py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                loading
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:bg-opacity-90"
              }`}
            >
              {passedRole ? "Update" : "Save"}
              {loading && (
                <span className="h-4 w-4 ml-2 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default RoleManagement;
